import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { useCurrency } from '../../../../../context/CurrencyContext';
import { Plus, Trash2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type FxConfig = {
  globalMarkupPct: number;
  ttlMinutes: number;
  staleMaxAgeHours: number;
  roundingProfile: string;
  enabled: boolean;
};
type KnownCurrency = { code: string; name: string };

function CurrenciesPage() {
  const { authorizedFetch } = useAuth();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );
  const { supportedCurrencies, refreshRates, refreshSupported, loading: contextLoading } = useCurrency();
  const { t } = useTranslation();
  const location = useLocation();

  const [addingCode, setAddingCode] = useState('');
  const [selectedSuggestionCode, setSelectedSuggestionCode] = useState<string | null>(null);
  const [knownSuggestions, setKnownSuggestions] = useState<KnownCurrency[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fxConfig, setFxConfig] = useState<FxConfig | null>(null);
  const [savingFxConfig, setSavingFxConfig] = useState(false);
  const [materializingFxPrices, setMaterializingFxPrices] = useState(false);
  const [manualBase, setManualBase] = useState('USD');
  const [manualQuote, setManualQuote] = useState('XAF');
  const [manualRate, setManualRate] = useState('');
  const [manualReason, setManualReason] = useState('');
  const [savingManual, setSavingManual] = useState(false);
  const [opsMessage, setOpsMessage] = useState<string | null>(null);
  const [confirmRemoveCode, setConfirmRemoveCode] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    const q = addingCode.trim();
    if (q.length < 1) {
      setKnownSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      void (async () => {
        setSuggestionsLoading(true);
        try {
          const url = new URL(`${API_BASE}/currencies/known`);
          url.searchParams.set('search', q);
          const res = await globalAuthorizedFetch(url.toString());
          if (!res.ok) return;
          const data = (await res.json()) as KnownCurrency[];
          setKnownSuggestions(data.slice(0, 8));
        } catch {
          // ignore
        } finally {
          setSuggestionsLoading(false);
        }
      })();
    }, 200);
    return () => clearTimeout(timer);
  }, [addingCode, globalAuthorizedFetch]);

  useEffect(() => {
    void (async () => {
      try {
        const res = await globalAuthorizedFetch(`${API_BASE}/currencies/fx-config`);
        if (!res.ok) return;
        const data = (await res.json()) as FxConfig;
        setFxConfig(data);
      } catch {
        // ignore
      }
    })();
  }, [globalAuthorizedFetch]);

  const handleAdd = async (rawCode?: string) => {
    const code = (rawCode ?? addingCode).trim().toUpperCase();
    if (!code) {
      setError('Currency code is required.');
      return;
    }
    if (!/^[A-Z]{3}$/.test(code)) {
      setError('Currency code must be a valid 3-letter ISO code (e.g. USD, EUR, NGN).');
      return;
    }
    setAdding(true);
    setError(null);
    setOpsMessage(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/currencies/supported`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || 'Failed to add currency');
      }
      await refreshRates();
      await refreshSupported();
      setAddingCode('');
      setSelectedSuggestionCode(null);
      setKnownSuggestions([]);
      setOpsMessage(`${code} added to supported currencies.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (code: string) => {
    setRemoving(code);
    setError(null);
    setOpsMessage(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/currencies/supported/${encodeURIComponent(code)}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || 'Failed to remove');
      }
      await refreshRates();
      await refreshSupported();
      setOpsMessage(`${code} removed from supported currencies.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setRemoving(null);
      setConfirmRemoveCode(null);
    }
  };

  const saveFxConfig = async () => {
    if (!fxConfig) return;
    setSavingFxConfig(true);
    setError(null);
    setOpsMessage(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/currencies/fx-config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fxConfig),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || 'Failed to update FX config');
      }
      const data = (await res.json()) as FxConfig;
      setFxConfig(data);
      await refreshRates();
      setOpsMessage('FX configuration saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSavingFxConfig(false);
    }
  };

  const materializeFxPrices = async () => {
    setMaterializingFxPrices(true);
    setError(null);
    setOpsMessage(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/currencies/fx-price-materialize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || 'Failed to materialize FX prices');
      }
      const data = (await res.json()) as {
        currencies: string[];
        productsUpdated: number;
        packagesUpdated: number;
      };
      setOpsMessage(
        `FX sale prices materialized for ${data.currencies.length} currencies (${data.productsUpdated} products, ${data.packagesUpdated} packages).`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to materialize FX prices');
    } finally {
      setMaterializingFxPrices(false);
    }
  };

  const saveManualOverride = async () => {
    const rate = Number(manualRate);
    if (!Number.isFinite(rate) || rate <= 0) {
      setError('Manual rate must be a positive number.');
      return;
    }
    setSavingManual(true);
    setError(null);
    setOpsMessage(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/currencies/manual-overrides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          baseCurrency: manualBase.trim().toUpperCase(),
          quoteCurrency: manualQuote.trim().toUpperCase(),
          rate,
          reason: manualReason.trim() || undefined,
          active: true,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || 'Failed to save manual override');
      }
      setOpsMessage(`Manual override saved: 1 ${manualBase.toUpperCase()} = ${rate} ${manualQuote.toUpperCase()}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSavingManual(false);
    }
  };

  const resetToUsdOnly = async () => {
    setResetting(true);
    setError(null);
    setOpsMessage(null);
    try {
      const removable = supportedCurrencies.filter((c) => c.code !== 'USD');
      for (const c of removable) {
        const res = await globalAuthorizedFetch(`${API_BASE}/currencies/supported/${encodeURIComponent(c.code)}`, {
          method: 'DELETE',
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error((err as { message?: string }).message || `Failed to remove ${c.code}`);
        }
      }
      await refreshRates();
      await refreshSupported();
      setOpsMessage('Reset complete. Only USD remains supported.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reset currencies.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.currencies_title', 'Currencies')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            Enterprise FX console: supported currency allowlist, real-time conversion policy, and manual rate fallback.
          </p>
        </section>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        {opsMessage && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
            {opsMessage}
          </div>
        )}

        <section className="workspace-panel">
          <h2 className="mb-3 text-lg font-semibold text-slate-800">
            Currency allowlist
          </h2>
          <p className="mb-3 text-sm text-slate-600">
            Enable currencies directly by ISO code. Search suggestions show full currency names.
          </p>
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start">
            <div className="w-full sm:max-w-xs">
              <input
                type="text"
                value={addingCode}
                onChange={(e) => {
                  setAddingCode(e.target.value.toUpperCase());
                  setSelectedSuggestionCode(null);
                }}
                placeholder="Enter ISO code (e.g. USD)"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              {addingCode.trim() ? (
                <div className="mt-1 max-h-44 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                  {suggestionsLoading ? (
                    <p className="px-3 py-2 text-xs text-slate-500">Loading suggestions…</p>
                  ) : knownSuggestions.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-slate-500">No currency suggestion.</p>
                  ) : (
                    knownSuggestions.map((s) => (
                      <button
                        key={s.code}
                        type="button"
                        onClick={() => {
                          setAddingCode(s.code);
                          setSelectedSuggestionCode(s.code);
                        }}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-xs hover:bg-slate-50"
                      >
                        <span className="font-semibold text-slate-800">{s.code}</span>
                        <span className="ml-2 truncate text-slate-600">{s.name}</span>
                      </button>
                    ))
                  )}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => handleAdd()}
              disabled={adding || !selectedSuggestionCode || selectedSuggestionCode !== addingCode.trim().toUpperCase()}
              className="inline-flex h-[42px] items-center justify-center gap-1 self-start rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" />
              {adding ? 'Adding…' : 'Enable currency'}
            </button>
          </div>
          <div className="mb-4 flex flex-wrap gap-2">
            {['EUR', 'GBP', 'CAD'].map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => handleAdd(code)}
                disabled={adding}
                className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
              >
                + {code}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={resetToUsdOnly}
            disabled={resetting}
            className="mb-4 rounded-md border border-rose-300 bg-white px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-60"
          >
            {resetting ? 'Resetting…' : 'Reset to USD only'}
          </button>

          <h2 className="mb-3 text-lg font-semibold text-slate-800">
            {t('backoffice.currencies_supported', 'Supported currencies')}
          </h2>
          {contextLoading ? (
            <p className="text-sm text-slate-500">{t('backoffice.currencies_loading', 'Loading…')}</p>
          ) : supportedCurrencies.length === 0 ? (
            <p className="text-sm text-slate-500">{t('backoffice.currencies_none', 'No currencies added yet.')}</p>
          ) : (
            <ul className="space-y-2">
              {supportedCurrencies.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm"
                >
                  <span className="font-medium text-slate-800">
                    {c.code}
                    {c.name && <span className="ml-2 text-slate-500">— {c.name}</span>}
                  </span>
                  {c.code !== 'USD' && (
                    <button
                      type="button"
                      onClick={() => setConfirmRemoveCode(c.code)}
                      disabled={!!removing}
                      className="inline-flex items-center gap-1 rounded border border-red-200 bg-white px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {removing === c.code ? t('common.removing', 'Removing…') : t('common.remove', 'Remove')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="workspace-panel">
          <h2 className="mb-3 text-lg font-semibold text-slate-800">
            Professional FX configuration
          </h2>
          {!fxConfig ? (
            <p className="text-sm text-slate-500">Loading configuration…</p>
          ) : (
            <div className="grid gap-3">
              <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={Boolean(fxConfig.enabled)}
                  onChange={(e) =>
                    setFxConfig((p) => (p ? { ...p, enabled: e.target.checked } : p))
                  }
                />
                <span className="text-xs text-slate-700">
                  <span className="block font-semibold">Enable FX for new users</span>
                  <span className="text-slate-500">
                    When disabled, FX conversion is turned off globally and all users stay on USD.
                  </span>
                </span>
              </label>
              <label className="text-xs font-semibold text-slate-700">
                FX markup (% for auto conversion)
                <input
                  type="number"
                  step="0.1"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={fxConfig.globalMarkupPct}
                  onChange={(e) => setFxConfig((p) => (p ? { ...p, globalMarkupPct: Number(e.target.value) } : p))}
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-xs font-semibold text-slate-700">
                  Fresh TTL (minutes)
                  <input
                    type="number"
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={fxConfig.ttlMinutes}
                    onChange={(e) => setFxConfig((p) => (p ? { ...p, ttlMinutes: Number(e.target.value) } : p))}
                  />
                </label>
                <label className="text-xs font-semibold text-slate-700">
                  Stale max age (hours)
                  <input
                    type="number"
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={fxConfig.staleMaxAgeHours}
                    onChange={(e) => setFxConfig((p) => (p ? { ...p, staleMaxAgeHours: Number(e.target.value) } : p))}
                  />
                </label>
              </div>
              <label className="text-xs font-semibold text-slate-700">
                Rounding profile
                <select
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                  value={fxConfig.roundingProfile}
                  onChange={(e) => setFxConfig((p) => (p ? { ...p, roundingProfile: e.target.value } : p))}
                >
                  <option value="PSYCH_99">Psychological .99</option>
                  <option value="PSYCH_95">Psychological .95</option>
                  <option value="ROUND_INT">Round integer</option>
                </select>
              </label>
              <button
                type="button"
                onClick={saveFxConfig}
                disabled={savingFxConfig}
                className="w-fit rounded-md border border-brand-500 bg-white px-4 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-60"
              >
                {savingFxConfig ? 'Saving…' : 'Save FX config'}
              </button>
              <button
                type="button"
                onClick={materializeFxPrices}
                disabled={materializingFxPrices || !fxConfig.enabled}
                className="w-fit rounded-md border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                {materializingFxPrices ? 'Materializing…' : 'Materialize FX sale prices'}
              </button>
            </div>
          )}
        </section>

        <section className="workspace-panel">
          <h2 className="mb-3 text-lg font-semibold text-slate-800">
            Manual rate override
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-semibold text-slate-700">
              Base currency
              <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={manualBase} onChange={(e) => setManualBase(e.target.value)} />
            </label>
            <label className="text-xs font-semibold text-slate-700">
              Quote currency
              <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={manualQuote} onChange={(e) => setManualQuote(e.target.value)} />
            </label>
          </div>
          <label className="mt-3 block text-xs font-semibold text-slate-700">
            Rate
            <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={manualRate} onChange={(e) => setManualRate(e.target.value)} />
          </label>
          <label className="mt-3 block text-xs font-semibold text-slate-700">
            Reason
            <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={manualReason} onChange={(e) => setManualReason(e.target.value)} />
          </label>
          <button
            type="button"
            onClick={saveManualOverride}
            disabled={savingManual}
            className="mt-3 rounded-md border border-brand-500 bg-white px-4 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-60"
          >
            {savingManual ? 'Saving…' : 'Save manual override'}
          </button>
        </section>

        {confirmRemoveCode ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 px-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
              <h2 className="text-base font-semibold text-slate-900">Remove currency</h2>
              <p className="mt-3 text-sm text-slate-600">
                Remove <strong>{confirmRemoveCode}</strong> from supported currencies?
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmRemoveCode(null)}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:border-slate-400"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleRemove(confirmRemoveCode)}
                  disabled={removing === confirmRemoveCode}
                  className="rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-500 disabled:opacity-60"
                >
                  {removing === confirmRemoveCode ? 'Removing…' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </main>
    </MainLayout>
  );
}

export default CurrenciesPage;

